import { auth } from 'src/api/client';
import { BASE_API_URL } from 'src/app/environment';
import type { BillingCycle, SubscriptionInfo } from './types';

async function fetchWithAuth<T>(
  path: string,
  body: Record<string, unknown>,
): Promise<T> {
  if (!auth.currentUser) {
    window.location.assign('/auth/login');
    throw new Error('User not authenticated');
  }
  const token = await auth.currentUser.getIdToken();
  const endpoint = `${BASE_API_URL}${path}`;
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      await auth.signOut();
      window.location.assign('/auth/login');
      throw new Error('Unauthorized. Redirecting to login.');
    }
    const text = await res.text();
    throw new Error(`Request failed (${res.status}): ${text}`);
  }
  return (await res.json()) as T;
}

export async function startTrial(
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  billingCycle: BillingCycle = 'monthly',
): Promise<SubscriptionInfo> {
  return fetchWithAuth<SubscriptionInfo>('/billing/start-trial', {
    billingCycle,
  });
}

export async function createCheckoutSession(params: {
  plan: BillingCycle;
  successUrl?: string;
  cancelUrl?: string;
}): Promise<{ id: string; url: string | null }> {
  return fetchWithAuth<{ id: string; url: string | null }>(
    '/billing/create-checkout-session',
    {
      plan: params.plan,
      success_url: params.successUrl,
      cancel_url: params.cancelUrl,
    },
  );
}

export async function upgradeNow(plan?: BillingCycle): Promise<void> {
  if (!auth.currentUser) {
    window.location.assign('/auth/login');
    return;
  }
  const chosenPlan = plan || 'monthly';
  const origin = window.location.origin;
  const { url } = await createCheckoutSession({
    plan: chosenPlan,
    successUrl: `${origin}/home/billing?session=success`,
    cancelUrl: `${origin}/home/billing?session=cancel`,
  });
  if (url) window.location.assign(url);
}

export async function startLocationTrial(params: {
  billingCycle?: BillingCycle;
  locationKey: string;
  planId?: string;
}): Promise<SubscriptionInfo> {
  return fetchWithAuth<SubscriptionInfo>('/billing/start-location-trial', {
    billingCycle: params.billingCycle ?? 'monthly',
    locationKey: params.locationKey,
    planId: params.planId,
  });
}

export async function createLocationCheckoutSession(params: {
  plan: BillingCycle;
  locationKey: string;
  planId?: string;
  successUrl?: string;
  cancelUrl?: string;
}): Promise<{ id: string; url: string | null }> {
  return fetchWithAuth<{ id: string; url: string | null }>(
    '/billing/create-location-checkout-session',
    {
      plan: params.plan,
      locationKey: params.locationKey,
      planId: params.planId,
      success_url: params.successUrl,
      cancel_url: params.cancelUrl,
    },
  );
}

export async function upgradeLocation(
  locationKey: string,
  plan: BillingCycle = 'monthly',
  planId?: string,
): Promise<void> {
  if (!auth.currentUser) {
    window.location.assign('/auth/login');
    return;
  }
  const chosenPlan = plan;
  const origin = window.location.origin;
  const { url } = await createLocationCheckoutSession({
    plan: chosenPlan,
    locationKey,
    planId,
    successUrl: `${origin}/home/billing?session=success&location=${encodeURIComponent(
      locationKey,
    )}`,
    cancelUrl: `${origin}/home/billing?session=cancel&location=${encodeURIComponent(
      locationKey,
    )}`,
  });
  if (url) window.location.assign(url);
}
